import "./scss/veracity.scss";

import React, { Component, FC, useEffect, useRef, useState } from "react";
import {
	BrowserRouter,
	Route,
	Routes,
	useLocation,
	Location as ReactRouterDomLocation,
	NavLink,
	matchRoutes,
} from "react-router-dom";
import {
	createGlobalStyle,
	extendTheme,
	LinksProvider,
	styled,
	th,
	useDown,
	useLinks,
	useLoadLinks,
	useUp,
	v,
	vui,
	VuiProvider,
	LoggedOutHeader,
	Box,
	Spinner,
	Notification,
	P,
	Footer,
	LoggedInHeader,
	useHeaderLinks,
	HeaderAccountData,
	useFooterLinks,
	H1,
	List,
	ListItemProps,
	IconProp,
	Header,
	Menu,
	ButtonIcon,
	hexToRGBA,
	colors,
} from "@veracity/vui";

import overrides from "./custom/overrides";

import { Observer } from "mobx-react-lite";
import { Page } from "./components/Page";

import { identityClient } from ".";
import { SignUpPage } from "./pages/SignUpPage";
import { ModalContainer } from "./components/ModalContainer";
import {
	OVERVIEW_PATH,
	COMPLETE_ENROLLMENT_PATH,
	COMPLETE_SIGN_UP_PATH_ALIASES,
	FORGOT_PASSWORD_PATH,
	INVITE_PATH,
	LIMITED_USERS_CHANGE_EMAIL_PATH,
	LIMITED_USERS_CHANGE_PASSWORD_PATH,
	LIMITED_USERS_VERIFY_EMAIL_PATH,
	NOTIFICATIONS_PATH,
	USER_PROFILE_PATH,
	RESET_PASSWORD_PATH,
	SECURITY_PATH,
	SIGN_UP_PATH,
	SIGN_UP_PATH_ALIASES,
	TERMS_PATH,
	TERMS_OF_USE_PATH,
	UPDATE_PASSWORD_PATH,
	VERIFY_EMAIL_PATH,
	ACTIVITY_PATH,
	SIGN_OUT_PATH,
	PREFERENCES_PATH,
	LOST_TOTP_PATH,
	RESET_TOTP_PATH,
	PHONE_RECOVERY_ENROLLMENT_PATH,
	PHONE_RECOVERY_COMPLETE_PATH,
} from "./misc/auto-generated/constants";
import { observable, remove } from "mobx";
import { CompleteSignupPage } from "./pages/CompleteSignupPage";
import { OverviewPage } from "./pages/OverviewPage";
import {
	OVERVIEW_ROUTE,
	USER_PROFILE_ROUTE,
	Route as IdentityRoute,
	ROUTES,
	SECURITY_ROUTE,
	TERMS_ROUTE,
} from "./routes";
import { UserProfilePage } from "./pages/UserProfilePage";
import { PreferencesPage } from "./pages/PreferencesPage";
import { TermsPage } from "./pages/TermsPage";
import { SecurityPage } from "./pages/SecurityPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { TermsOfUsePage } from "./pages/TermsOfUsePage";
import { ErrorInfo } from "./components/ErrorInfo";
import { CompleteEnrollmentPage } from "./pages/CompleteEnrollmentPage";
import { UpdatePasswordPage } from "./pages/UpdatePasswordPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { LimitedUsersChangeEmailPage } from "./pages/LimitedUsersChangeEmailPage";
import { LimitedUsersVerifyEmailPage } from "./pages/LimitedUsersVerifyEmailPage";
import { LimitedUsersChangePasswordPage } from "./pages/LimitedUsersChangePasswordPage";
import { InvitePage } from "./pages/InvitePage";
import { getLocationWithLoggedInParameterSet } from "./utils/getLocationWithLoggedInParameterSet";
import { LostTotpPage } from "./pages/LostTotpPage";
import { ResetTotpPage } from "./pages/ResetTotpPage";
import { PhoneRecoveryEnrollmentPage } from "./pages/PhoneRecoveryEnrollmentPage";
import { PhoneRecoveryCompletePage } from "./pages/PhoneRecoveryCompletePage";
//import { ActivityPage } from "./pages/ActivityPage";

export const theme = extendTheme(overrides);

export default function App() {
	return (
		<VuiProvider theme={theme}>
			<Observer>
				{() => {
					if (identityClient.initializationStatus?.isInitializing)
						return (
							<Page center>
								<Spinner text="Loading Veracity Identity..." />
							</Page>
						);

					return <LoadedApp />;
				}}
			</Observer>
			<ModalContainer />
		</VuiProvider>
	);
}

function LoadedApp() {
	useLoadLinks(VUI_ENVIRONMENT, !!identityClient.loggedInUserProfile);

	return (
		<v.div
			animation="fadeIn"
			flex="1"
			display="flex"
			flexDirection="column"
		>
			<ErrorBoundary>
				<BrowserRouter>
					<Routes>
						<Route
							path={FORGOT_PASSWORD_PATH}
							element={<ForgotPasswordPage />}
						/>
						<Route
							path={LOST_TOTP_PATH}
							element={<LostTotpPage />}
						/>
						<Route
							path={RESET_PASSWORD_PATH}
							element={<ResetPasswordPage />}
						/>
						<Route
							path={RESET_TOTP_PATH}
							element={<ResetTotpPage />}
						/>
						<Route
							path="/limited-users/*"
							element={<HeaderAndLimitedUsersPages />}
						/>
						<Route path="*" element={<HeaderFooterPages />} />
					</Routes>
				</BrowserRouter>
			</ErrorBoundary>
		</v.div>
	);
}

function IdentityLoggedInHeader() {
	let { mainLinks, accountSections = [] } = useHeaderLinks(VUI_ENVIRONMENT);
	let [isAdmin, setIsAdmin] = useState(false);
	let [withoutUsersAndPermissions, setWithoutUsersAndPermissions] = useState([{"links": []}]);

	useEffect(() => {
		const fetchIsAdmin = async () => {
			const isAdmin = await identityClient.fetchIsAdminAsync();
			setIsAdmin(isAdmin)
		}

		fetchIsAdmin()
	}, [])

	useEffect(() => {
		if (accountSections.length > 0 && withoutUsersAndPermissions[0].links.length == 0) {
			const removeUsersAndPermissions = accountSections[0]?.links?.filter((link) => link.id != "usersAndPermissions")
			if (removeUsersAndPermissions != null) setWithoutUsersAndPermissions([{"links": removeUsersAndPermissions}])	
		}
	}, [accountSections])

	return (
		<Observer>
			{() => {
				let accountLinks = isAdmin ? accountSections : withoutUsersAndPermissions
				let account = {
					sections: [
						...accountLinks,
						{
							links: [
								{
									icon: "falSignOut",
									text: "Sign out",
									url: SIGN_OUT_PATH,
								},
							],
						},
					],
					userInfo: {
						displayName:
							identityClient.loggedInUserProfile?.dto.DisplayName,
						companyName: identityClient.companies?.length
							? identityClient.companies[0].Name
							: null,
					},
				} as HeaderAccountData;

				return (
					<LoggedInHeader
						mainLinks={mainLinks}
						notifications={{ count: identityClient.messageCount }}
						services={{
							links: identityClient.services
								? identityClient.services.map((s) => ({
										text: s.Name,
										url: s.ServiceUrl,
								  }))
								: [],
						}}
						account={account}
					/>
				);
			}}
		</Observer>
	);
}

function IdentityLoggedOutHeader() {
	let { mainLinks } = useHeaderLinks(VUI_ENVIRONMENT);
	let location = useLocation();

	return (
		<LoggedOutHeader
			mainLinks={mainLinks}
			signIn={{ url: getLocationWithLoggedInParameterSet(location) }}
		/>
	);
}

export function IdentityFooter() {
	let footerLinks = useFooterLinks(VUI_ENVIRONMENT);

	return <Footer {...footerLinks} />;
}

function HeaderFooterPages() {
	let isMobile = useDown("md");
	let location = useLocation();
	let [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
	let routes = matchRoutes(
		ROUTES.map((r) => ({ path: r.path, identityRoute: r })),
		location
	);

	function generateMenuButtonBasedOnMatchingRoute() {
		if (routes?.length) {
			let identityRoute = (
				routes[0].route as any as { identityRoute: IdentityRoute }
			)?.identityRoute;
			return (
				<Menu.Button
					variant="secondaryDark"
					colorScheme="prussian"
					bg="white"
					iconLeft={identityRoute?.iconName}
					iconRight={
						<ButtonIcon
							ml="auto"
							name="falAngleDown"
							color="blue.80"
						/>
					}
					w="100%"
				>
					{identityRoute?.title}
				</Menu.Button>
			);
		}
		return (
			<Menu.Button
				variant="secondaryDark"
				colorScheme="prussian"
				bg="white"
				iconRight={
					<ButtonIcon ml="auto" name="falAngleDown" color="blue.80" />
				}
				w="100%"
			></Menu.Button>
		);
	}

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location]);

	let headerElement = (
		<Observer>
			{() =>
				identityClient.loggedInUserProfile ? (
					<IdentityLoggedInHeader />
				) : (
					<IdentityLoggedOutHeader />
				)
			}
		</Observer>
	);

	return (
		<>
			{isMobile ? (
				<v.div position="sticky" top="0" zIndex="2">
					{headerElement}

					{/* MOBILE MENU */}
					{!!routes?.length && (
						<v.div
							p={1.75}
							bg="blue.20"
							borderBottom="1px solid grey.40"
						>
							<Menu
								matchWidth
								onOpen={() => setMobileMenuIsOpen(true)}
								onClose={() => setMobileMenuIsOpen(false)}
							>
								{generateMenuButtonBasedOnMatchingRoute()}
								<Menu.List maxH={400}>
									{ROUTES.map((r, i) => (
										<Menu.Item
											key={i}
											as={NavLink}
											to={r.path}
											iconLeft={r.iconName}
										>
											{r.title}
										</Menu.Item>
									))}
								</Menu.List>
							</Menu>
						</v.div>
					)}
				</v.div>
			) : (
				headerElement
			)}
			<v.div
				flex="1"
				display="flex"
				flexDirection="column"
				position="relative"
			>
				{isMobile && !!routes?.length && mobileMenuIsOpen && (
					<v.div
						position="absolute"
						top="0"
						right="0"
						bottom="0"
						left="0"
						bg={hexToRGBA(colors.prussian[80], 0.6)}
						userSelect="none"
						zIndex="1"
						animation="vui-fadeIn 0.3s ease"
					/>
				)}
				<Routes>
					{SIGN_UP_PATH_ALIASES.map((p) => (
						<Route key={p} path={p} element={<SignUpPage />} />
					))}
					{COMPLETE_SIGN_UP_PATH_ALIASES.map((p) => (
						<Route
							key={p}
							path={p}
							element={<CompleteSignupPage />}
						/>
					))}
					<Route
						path={COMPLETE_ENROLLMENT_PATH}
						element={<CompleteEnrollmentPage />}
					/>
					<Route
						path={UPDATE_PASSWORD_PATH}
						element={<UpdatePasswordPage />}
					/>
					<Route
						path={TERMS_OF_USE_PATH}
						element={<TermsOfUsePage />}
					/>
					<Route
						path={PHONE_RECOVERY_ENROLLMENT_PATH}
						element={<PhoneRecoveryEnrollmentPage />}
					/>
					<Route
						path={PHONE_RECOVERY_COMPLETE_PATH}
						element={<PhoneRecoveryCompletePage />}
					/>
					<Route
						path={VERIFY_EMAIL_PATH}
						element={<VerifyEmailPage />}
					/>
					<Route path={INVITE_PATH} element={<InvitePage />} />
					<Route
						path="*"
						element={
							<HeaderFooterLoggedInPages
								showDesktopMenu={!isMobile && !!routes?.length}
							/>
						}
					/>
				</Routes>
				<IdentityFooter />
			</v.div>
		</>
	);
}

function HeaderFooterLoggedInPages(props: { showDesktopMenu: boolean }) {
	return (
		<v.div flex="1" display={props.showDesktopMenu ? "flex" : null}>
			{/* DESKTOP MENU */}
			{props.showDesktopMenu && (
				<List
					w="320px"
					borderRight="1px solid grey.40"
					size="navItem"
					isInteractive
				>
					{ROUTES.map((r, i) => (
						<NavItem
							key={i}
							to={r.path}
							icon={r.iconName}
							text={r.title}
						/>
					))}
				</List>
			)}

			<v.div
				flex="1"
				display="flex"
				bg="hsl(196, 74%, 97%)"
				minH={
					props.showDesktopMenu
						? "calc(100vh - 72px)"
						: `calc(100vh - ${72 + 69}px)`
				}
			>
				<Routes>
					<Route path={OVERVIEW_PATH} element={<OverviewPage />} />
					<Route
						path={USER_PROFILE_PATH}
						element={<UserProfilePage />}
					/>
					<Route
						path={PREFERENCES_PATH}
						element={<PreferencesPage />}
					/>
					<Route path={TERMS_PATH} element={<TermsPage />} />
					<Route path={SECURITY_PATH} element={<SecurityPage />} />
					<Route
						path={NOTIFICATIONS_PATH}
						element={<NotificationsPage />}
					/>
					{/* <Route path={ACTIVITY_PATH} element={<ActivityPage />} /> */}
				</Routes>
			</v.div>
		</v.div>
	);
}

const NavItemBase = styled(List.Item)`
	position: relative;

	&:before {
		background: ${th.color("grey.40")};
		bottom: 0;
		content: "";
		height: 1px;
		left: 0;
		position: absolute;
		right: 0;
	}

	&:after {
		background: transparent;
		bottom: 0;
		content: "";
		left: 0;
		position: absolute;
		top: 0;
		width: 6px;
	}

	&.active {
		background-color: ${th.color("blue.20")};

		&:after {
			background: ${th.color("prussian.80")};
		}

		&:hover {
			background-color: ${th.color("blue.30")};
		}
	}
`;

function NavItem(props: { icon: IconProp; text; to: string }) {
	return (
		<NavItemBase
			forwardedAs={NavLink}
			to={props.to}
			pl={4}
			iconLeft={<List.Icon mr={2} name={props.icon} />}
		>
			{props.text}
		</NavItemBase>
	);
}

function HeaderAndLimitedUsersPages() {
	let location = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location]);

	return (
		<>
			<Header variant="dark">
				<Header.Content>
					<Header.Logo />
				</Header.Content>
			</Header>
			<Routes>
				<Route
					path={LIMITED_USERS_CHANGE_EMAIL_PATH.substring(
						"/limited-users/".length
					)}
					element={<LimitedUsersChangeEmailPage />}
				/>
				<Route
					path={LIMITED_USERS_VERIFY_EMAIL_PATH.substring(
						"/limited-users/".length
					)}
					element={<LimitedUsersVerifyEmailPage />}
				/>
				<Route
					path={LIMITED_USERS_CHANGE_PASSWORD_PATH.substring(
						"/limited-users/".length
					)}
					element={<LimitedUsersChangePasswordPage />}
				/>
			</Routes>
		</>
	);
}

class ErrorBoundary extends Component<{}, { threw: boolean; error? }> {
	constructor(props) {
		super(props);
		this.state = { threw: false };
	}

	static getDerivedStateFromError(error) {
		return { threw: true, error };
	}

	componentDidCatch(error, errorInfo) {
		if (IS_DEVELOPMENT) console.debug(error, errorInfo);
	}

	render() {
		if (this.state.threw)
			return (
				<Page>
					<H1 mb={2}>Error</H1>
					<ErrorInfo error={this.state.error} />
				</Page>
			);

		return this.props.children;
	}
}
